import React, { useState } from 'react';
import { IAFITeamEntry } from '../../../models/afi';
import { AFI_CLASSES, getAFIClass, getAFIColor } from '../../constants/afi';
import AFIScoreBadge from './AFIScoreBadge';
import AFITrendArrow from './AFITrendArrow';
import styles from './AFITeamTable.module.scss';

interface AFITeamTableProps {
  athletes: IAFITeamEntry[];
  isLoading: boolean;
  onSelectAthlete: (athleteId: string, athleteName: string) => void;
}

type SortDir = 'desc' | 'asc';

const AFITeamTable: React.FC<AFITeamTableProps> = ({
  athletes,
  isLoading,
  onSelectAthlete,
}) => {
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = () => {
    setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const sorted = [...athletes].sort((a, b) =>
    sortDir === 'desc' ? b.afiScore - a.afiScore : a.afiScore - b.afiScore
  );

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thNum}>#</th>
              <th className={styles.thAthlete}>Atleta</th>
              <th className={styles.thScore}>AFI Score</th>
              <th className={styles.thTrend}>Trend</th>
              <th className={styles.thComp}>Comp. A</th>
              <th className={styles.thComp}>Comp. C</th>
              <th className={styles.thAlert}>Alert</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className={styles.skeletonRow}>
                <td><div className={styles.skeleton} style={{ width: 20 }} /></td>
                <td><div className={styles.skeleton} style={{ width: 140 }} /></td>
                <td><div className={styles.skeleton} style={{ width: 80 }} /></td>
                <td><div className={styles.skeleton} style={{ width: 60 }} /></td>
                <td><div className={styles.skeleton} style={{ width: 40 }} /></td>
                <td><div className={styles.skeleton} style={{ width: 40 }} /></td>
                <td><div className={styles.skeleton} style={{ width: 24 }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (athletes.length === 0) {
    return (
      <div className={styles.empty}>
        Nessun dato AFI disponibile
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.thNum}>#</th>
            <th className={styles.thAthlete}>Atleta</th>
            <th
              className={`${styles.thScore} ${styles.sortable}`}
              onClick={toggleSort}
              title="Ordina per AFI Score"
            >
              AFI Score {sortDir === 'desc' ? '↓' : '↑'}
            </th>
            <th className={styles.thTrend}>Trend</th>
            <th className={styles.thComp}>Comp. A</th>
            <th className={styles.thComp}>Comp. C</th>
            <th className={styles.thAlert}>Alert</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((athlete, idx) => {
            const afiClass = getAFIClass(athlete.afiScore);
            const classCfg = AFI_CLASSES[afiClass];
            const hasCriticalAlert = (athlete.alerts ?? []).some((a) => a.severity === 'critical');
            const hasAnyAlert = (athlete.alerts ?? []).length > 0;

            const rowStyle: React.CSSProperties = {
              backgroundColor: `${classCfg.bg}4d`,
            };

            return (
              <tr
                key={athlete.athleteId}
                className={`${styles.row} ${hasCriticalAlert ? styles.criticalBorder : ''}`}
                style={rowStyle}
                onClick={() => onSelectAthlete(athlete.athleteId, athlete.athleteName)}
              >
                <td className={styles.tdNum}>{idx + 1}</td>
                <td className={styles.tdAthlete}>
                  {athlete.athleteImage && (
                    <img
                      src={athlete.athleteImage}
                      alt={athlete.athleteName}
                      className={styles.avatar}
                    />
                  )}
                  <span className={styles.athleteName}>{athlete.athleteName}</span>
                </td>
                <td className={styles.tdScore}>
                  <AFIScoreBadge score={athlete.afiScore} size="md" />
                </td>
                <td className={styles.tdTrend}>
                  <AFITrendArrow
                    trend={athlete.trend}
                    delta={athlete.trendDelta}
                    showDelta
                  />
                </td>
                <td className={styles.tdComp}>
                  {athlete.componentA !== null ? (
                    <span style={{ color: getAFIColor(athlete.componentA), fontWeight: 600 }}>
                      {athlete.componentA}
                    </span>
                  ) : (
                    <span className={styles.noData}>N/D</span>
                  )}
                </td>
                <td className={styles.tdComp}>
                  {athlete.componentC !== null ? (
                    <span style={{ color: getAFIColor(athlete.componentC), fontWeight: 600 }}>
                      {athlete.componentC}
                    </span>
                  ) : (
                    <span className={styles.noData}>N/D</span>
                  )}
                </td>
                <td className={styles.tdAlert}>
                  {hasAnyAlert && (
                    <span
                      className={styles.alertIcon}
                      title={`${athlete.alerts.length} alert`}
                    >
                      ⚠️
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AFITeamTable;
